import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

type DisplayCurrency = "EUR" | "USD" | "CHF";

interface CurrencyContextType {
  displayCurrency: DisplayCurrency;
  setDisplayCurrency: (c: DisplayCurrency) => void;
  convert: (centimes: number, fromCurrency: DisplayCurrency) => number;
  formatConverted: (centimes: number, fromCurrency: DisplayCurrency) => string;
}

const FALLBACK_RATES: Record<string, number> = {
  EUR_USD: 1.08, EUR_CHF: 0.96, USD_EUR: 0.93, USD_CHF: 0.89, CHF_EUR: 1.04, CHF_USD: 1.12,
};

const CACHE_KEY = "ehs_exchange_rates";
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

const CurrencyContext = createContext<CurrencyContextType | null>(null);

function getCachedRates(): Record<string, number> | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { rates, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < CACHE_TTL) return rates;
  } catch {}
  return null;
}

function setCachedRates(rates: Record<string, number>) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ rates, timestamp: Date.now() }));
  } catch {}
}

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>("EUR");
  const [rates, setRates] = useState<Record<string, number>>(() => getCachedRates() || FALLBACK_RATES);

  useEffect(() => {
    // Skip fetch if we have fresh cached rates
    if (getCachedRates()) return;

    fetch("https://api.exchangerate-api.com/v4/latest/EUR")
      .then(r => r.json())
      .then(data => {
        if (data?.rates) {
          const newRates = {
            EUR_USD: data.rates.USD, EUR_CHF: data.rates.CHF,
            USD_EUR: 1 / data.rates.USD, USD_CHF: data.rates.CHF / data.rates.USD,
            CHF_EUR: 1 / data.rates.CHF, CHF_USD: data.rates.USD / data.rates.CHF,
          };
          setRates(newRates);
          setCachedRates(newRates);
        }
      })
      .catch(() => {});
  }, []);

  const convert = useCallback((centimes: number, fromCurrency: DisplayCurrency): number => {
    if (fromCurrency === displayCurrency) return centimes;
    const key = `${fromCurrency}_${displayCurrency}`;
    const rate = rates[key] || 1;
    return Math.round(centimes * rate);
  }, [displayCurrency, rates]);

  const formatConverted = useCallback((centimes: number, fromCurrency: DisplayCurrency): string => {
    const converted = convert(centimes, fromCurrency);
    const amount = converted / 100;
    return new Intl.NumberFormat(
      displayCurrency === "EUR" ? "de-DE" : displayCurrency === "USD" ? "en-US" : "de-CH",
      { style: "currency", currency: displayCurrency, minimumFractionDigits: 0, maximumFractionDigits: 0 }
    ).format(amount);
  }, [convert, displayCurrency]);

  return (
    <CurrencyContext.Provider value={{ displayCurrency, setDisplayCurrency, convert, formatConverted }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
};
